import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  HomeIcon, 
  BeakerIcon, 
  CubeIcon, 
  DocumentTextIcon, 
  CubeTransparentIcon, 
  BuildingStorefrontIcon,
  CogIcon,
  ChartBarIcon,
  UserGroupIcon,
  GiftIcon,
  QrCodeIcon,
  TicketIcon,
  ShieldCheckIcon,
  BellIcon,
  ClipboardDocumentListIcon,
  RectangleStackIcon,
  Bars3Icon, 
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    dashboard: false,
    production: false,
    sales: false,
    fidelity: false
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const navigationItems = [
    {
      section: 'dashboard',
      name: 'Dashboard',
      icon: HomeIcon,
      children: [
        { path: '/', name: 'Panoramica', icon: HomeIcon },
        { path: '/users', name: 'Gestione Utenti', icon: ShieldCheckIcon },
        { path: '/notifications', name: 'Notifiche Push', icon: BellIcon }
      ]
    },
    {
      section: 'production',
      name: 'Gestione Produzione',
      icon: CubeTransparentIcon,
      children: [
        { path: '/ingredients', name: 'Ingredienti', icon: BeakerIcon },
        { path: '/packaging', name: 'Packaging', icon: CubeIcon },
        { path: '/recipes', name: 'Ricette', icon: DocumentTextIcon },
        { path: '/products', name: 'Prodotti', icon: CubeTransparentIcon },
        { path: '/productions/new', name: 'Nuova Produzione', icon: ClipboardDocumentListIcon },
        { path: '/productions/history', name: 'Storico Produzioni', icon: RectangleStackIcon },
      ]
    },
    {
      section: 'sales',
      name: 'Vendite & Analisi',
      icon: ChartBarIcon,
      children: [
        { path: '/channels', name: 'Canali Vendita', icon: BuildingStorefrontIcon },
        { path: '/production', name: 'Calcolo Produzione', icon: CogIcon },
        { path: '/analysis', name: 'Analisi Margini', icon: ChartBarIcon },
      ]
    },
    {
      section: 'fidelity',
      name: 'Sistema Fidelity',
      icon: UserGroupIcon,
      children: [
        { path: '/fidelity/customers', name: 'Clienti', icon: UserGroupIcon },
        { path: '/fidelity/rewards', name: 'Catalogo Premi', icon: GiftIcon },
        { path: '/fidelity/scanner', name: 'Scanner QR', icon: QrCodeIcon },
        { path: '/fidelity/redemptions', name: 'Riscatti', icon: TicketIcon },
      ]
    }
  ];

  const isActive = (path) => location.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <ChartBarIcon className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-800">Analisi Lacinio</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigationItems.map((item, index) => {
              if (item.section) {
                // Section with children
                const Icon = item.icon;
                const isAnyChildActive = item.children.some(child => location.pathname === child.path);
                
                return (
                  <div key={item.section} className="relative group">
                    <div
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                        isAnyChildActive
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.name}</span>
                      <ChevronDownIcon className="w-3 h-3 transition-transform group-hover:rotate-180" />
                    </div>
                    
                    {/* Dropdown - solo hover, nessuno stato */}
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 hidden group-hover:block">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const isActive = location.pathname === child.path;
                        
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`flex items-center space-x-2 px-4 py-2 text-sm ${
                              isActive
                                ? 'bg-blue-50 text-blue-600 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <ChildIcon className="w-4 h-4" />
                            <span>{child.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              } else {
                // Simple link
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              }
            })}
          </div>

          {/* User info and logout */}
          <div className="hidden md:flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              {user?.full_name || user?.username}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200"
              title="Esci"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              <span>Esci</span>
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              data-testid="mobile-menu-toggle"
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-1">
              {navigationItems.map((item) => {
                if (item.section) {
                  // Section with children
                  const Icon = item.icon;
                  const isExpanded = expandedSections[item.section];
                  const isAnyChildActive = item.children.some(child => location.pathname === child.path);
                  
                  return (
                    <div key={item.section}>
                      <button
                        onClick={() => toggleSection(item.section)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isAnyChildActive
                            ? 'bg-blue-100 text-blue-700 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Icon className="w-5 h-5" />
                          <span>{item.name}</span>
                        </div>
                        <ChevronRightIcon className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </button>
                      
                      {/* Mobile Submenu */}
                      {isExpanded && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children.map((child) => {
                            const ChildIcon = child.icon;
                            const isActive = location.pathname === child.path;
                            
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm ${
                                  isActive
                                    ? 'bg-blue-50 text-blue-600 font-medium'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <ChildIcon className="w-4 h-4" />
                                <span>{child.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  // Simple link
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-blue-100 text-blue-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </Link>
                  );
                }
              })}
              
              {/* Logout mobile */}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleLogout();
                }}
                className="flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                <span>Esci ({user?.username})</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;